import type { MutableRefObject, ReactElement } from 'react';
import { useRef } from 'react';
import type { AudienceSegment, DocumentSection, Keyword, PlanPath, Proposal } from '@shared/types';
import type { PageActions } from '../types';
import { EditableMoney, EditableText } from '../../components/Editable';
import { count, money } from '../../lib/format';
import { channel, pricedLine, tacticDeliveryMetrics } from '../../lib/plan';
import { useBindings } from '../../state/BindingsContext';
import { fitScaleStyle, useFitScale, useSharedFitScale } from '../../hooks/useFitScale';
import { MagnifierIcon, PeopleIcon, ShieldIcon, landingExperienceIcon, planPathIcon } from './icons';
import { SponsoredSearchCard, keywordPick, searchHeroModel } from './SearchDeviceVisual';

/**
 * product_plan `dual_path` variant (`docs/momentum-v3-slides.md`
 * product_plan; media-pair-contract.md "Slide 10"). Two parallel conversion-
 * path cards + a metric rail, replacing the single-column "How we'll use
 * it" layout for tactics the agent judged have two distinct buyer intents.
 * `single_plan`/absent keeps `ProductPlanPage`'s existing layout untouched —
 * this file only renders when `section.content.variant === 'dual_path'`.
 *
 * SEM layout (2026-09-29, `.mv3-semplan`): keyword-first. Each path card
 * leads with its keywords as search-bar rows and lists its audiences with
 * their sized people counts; the header carries the plan's keyword mix by
 * intent; the rail shows a sponsored-search result for the top high-intent
 * keyword (`SponsoredSearchCard`), then audience size / clicks /
 * conversions, then the brand-defense callout. Every number is read from
 * saved data (keywords, `estimatedPeople`, pricing) — unsized means blank.
 *
 * Fixed renderer pattern (audit "Slide 10 core principle": "a fixed
 * renderer pattern, not an AI-generated diagram") — card count, icons,
 * arrows, dividers and the metric rail are all owned here; the agent
 * supplies only the path/callout copy in `content.paths` / `content.callout`.
 * Editable where cheap (media-pair-contract.md "Agent contract"): headline,
 * callout body, and each path's role/label via `EditableText` +
 * `actions.saveContent`; search-theme / audience chips and the landing→
 * conversion line are read-only, authored copy.
 *
 * Fix round 3: sizes are the contract's reference values again (round 2
 * had shrunk everything to fit the audit's worst case directly, which
 * left typical decks — most decks, including the real ad6abc03 sample —
 * looking half-empty with illegibly small type). Overflow for the worst
 * case (max chips, 2-line role, max-length callout) is instead handled by
 * `useFitScale`: the callout's content block measures itself against its
 * own fixed-height box and scales DOWN only when it doesn't fit, floored so
 * nothing goes illegible. The two path cards' content blocks are measured
 * the same way but share ONE scale (`useSharedFitScale`, the smaller of
 * their two independent natural fit scales) — otherwise a shorter card
 * would render its type visibly larger than its sibling, which reads as
 * broken even though neither card is individually wrong. A typical deck
 * renders at scale 1 (reference size, card filled); only the rare
 * over-long deck shrinks, and even then nothing is clipped or ellipsized —
 * see those hooks' own docs for the mechanism.
 */

/** The path's priority audiences (≤3), each with its sized people count when sizing has landed — null stays null, never a guess. */
function pathSegments(proposal: Proposal, ids: string[]): { id: string; name: string; people: number | null }[] {
  const byId = new Map(proposal.audience.segments.map((segment) => [segment.id, segment]));
  return ids
    .map((id) => byId.get(id))
    .filter((segment): segment is AudienceSegment => !!segment)
    .map((segment) => ({
      id: segment.id,
      name: segment.name || segment.segment || '',
      people: segment.estimatedPeople?.value ?? null,
    }))
    .filter((segment) => segment.name.length > 0)
    .slice(0, 3);
}

/**
 * Rail "Priority audience size": the sized people count summed over the
 * DISTINCT segments both paths target — the same sum `Forecast.audienceSize`
 * takes over the whole plan. Unsized segments are left out of the total and
 * counted separately so the note can say so; null when nothing is sized.
 */
function audienceTotal(proposal: Proposal, paths: PlanPath[]): { people: number; sized: number } | null {
  const ids = new Set(paths.flatMap((path) => path.audienceSegmentIds));
  let people = 0;
  let sized = 0;
  for (const segment of proposal.audience.segments) {
    const value = segment.estimatedPeople?.value;
    if (!ids.has(segment.id) || value === undefined || value === null || !Number.isFinite(value)) continue;
    people += value;
    sized += 1;
  }
  return sized > 0 ? { people, sized } : null;
}

const KEYWORD_GROUPS: { category: Keyword['category']; intent: string }[] = [
  { category: 'high_intent', intent: 'High intent' },
  { category: 'category', intent: 'Category' },
  { category: 'geo', intent: 'Local' },
  { category: 'branded', intent: 'Brand' },
  { category: 'competitor', intent: 'Competitor' },
];

/** Header keyword-mix strip: the saved plan keywords counted by intent group, empty groups dropped. */
function keywordMix(keywords: Keyword[]): { intent: string; count: number }[] {
  return KEYWORD_GROUPS.map((group) => ({
    intent: group.intent,
    count: keywords.filter((kw) => kw.category === group.category).length,
  })).filter((group) => group.count > 0);
}

function replacePath(paths: PlanPath[], index: number, patch: Partial<PlanPath>): PlanPath[] {
  return paths.map((path, i) => (i === index ? { ...path, ...patch } : path));
}

function PathCard({
  path,
  index,
  paths,
  proposal,
  section,
  actions,
  readOnly,
  innerRef,
  scale,
}: {
  path: PlanPath;
  index: number;
  paths: PlanPath[];
  proposal: Proposal;
  section: DocumentSection;
  actions: PageActions;
  readOnly: boolean;
  innerRef: MutableRefObject<HTMLDivElement | null>;
  scale: number;
}): ReactElement {
  const resolve = useBindings();
  const audiences = pathSegments(proposal, path.audienceSegmentIds);

  return (
    <div className="mv3-planpath-card">
      <div className="mv3-planpath-card-inner" ref={innerRef} style={fitScaleStyle(scale)}>
        <div className="mv3-planpath-heading">
          <span className="mv3-planpath-icon">{planPathIcon(path.icon, index, { size: 38 })}</span>
          <div className="mv3-planpath-heading-text">
            <div className="mv3-planpath-label">
              <EditableText
                value={path.label}
                field="headline"
                placeholder="Path label"
                readOnly={readOnly}
                variant="headline"
                onSave={(next) => actions.saveContent(section, { paths: replacePath(paths, index, { label: next }) })}
              />
            </div>
            <div className="mv3-planpath-role">
              <EditableText
                value={path.role}
                field="body"
                placeholder="What this path does"
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { paths: replacePath(paths, index, { role: next }) })}
              />
            </div>
          </div>
        </div>

        {path.searchThemes.length > 0 ? (
          <div className="mv3-planpath-block">
            <div className="mv3-planpath-eyebrow">Keywords we bid on</div>
            <div className="mv3-semplan-keywords">
              {path.searchThemes.map((theme) => (
                <div className="mv3-semplan-keyword" key={theme}>
                  <MagnifierIcon size={22} />
                  <span>{resolve(theme)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mv3-planpath-divider" />

        {/* Fixed 96px icon column (same width as the heading circle above)
            + a content column where the tracked label sits directly above
            its content, sharing ONE left edge - alignment fix round 5
            (`mv3-planpath-eyebrow-row`, which put the label beside the icon
            and left the content flush under the icon instead, is gone). */}
        <div className="mv3-planpath-block mv3-planpath-block-icon">
          <span className="mv3-planpath-icon-col">{landingExperienceIcon(index, { size: 56 })}</span>
          <div className="mv3-planpath-content-col">
            <div className="mv3-planpath-eyebrow">Landing experience</div>
            <div className="mv3-planpath-landing">
              <span>{resolve(path.landingLabel)}</span>
              <span className="mv3-planpath-arrow" aria-hidden="true">
                →
              </span>
              <span>{resolve(path.conversionLabel)}</span>
            </div>
          </div>
        </div>

        {audiences.length > 0 ? (
          <>
            <div className="mv3-planpath-divider" />
            <div className="mv3-planpath-block mv3-planpath-block-icon">
              <span className="mv3-planpath-icon-col">
                <PeopleIcon size={52} />
              </span>
              <div className="mv3-planpath-content-col">
                <div className="mv3-planpath-eyebrow">Priority audiences</div>
                <div className="mv3-semplan-audiences">
                  {audiences.map((audience) => (
                    <div className="mv3-semplan-audience" key={audience.id}>
                      <span className="mv3-semplan-audience-name">{resolve(audience.name)}</span>
                      {audience.people !== null ? (
                        <span className="mv3-semplan-audience-size">{count(audience.people)}</span>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Rail stat panel: audience size first (the slide's reach story), then the
 * cpc delivery estimates. One panel of label/value rows instead of three
 * StatTiles — the tiles' stacked label+value ran the rail past its box.
 * A null value drops its row; no rows, no panel.
 */
function SemStats({
  audience,
  clicks,
  conversions,
}: {
  audience: { people: number; sized: number } | null;
  clicks: number | null;
  conversions: number | null;
}): ReactElement | null {
  const rows: { key: string; label: string; note?: string; value: string | null; accent?: boolean }[] = [
    {
      key: 'audience',
      label: 'Priority audience size',
      note: audience ? `people across ${audience.sized} ${audience.sized === 1 ? 'audience' : 'audiences'}` : undefined,
      value: audience ? count(audience.people) : null,
      accent: true,
    },
    { key: 'clicks', label: 'Est. clicks / month', value: count(clicks) },
    { key: 'conversions', label: 'Est. conversions / month', value: count(conversions) },
  ].filter((row) => row.value !== null);
  if (rows.length === 0) return null;
  return (
    <div className="mv3-semplan-stats">
      {rows.map((row) => (
        <div className={`mv3-semplan-stat${row.accent ? ' mv3-semplan-stat-accent' : ''}`} key={row.key}>
          <div className="mv3-semplan-stat-text">
            <span className="mv3-semplan-stat-label">{row.label}</span>
            {row.note ? <span className="mv3-semplan-stat-note">{row.note}</span> : null}
          </div>
          <span className="mv3-semplan-stat-value">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

export function DualPathPlan({
  proposal,
  section,
  actions,
  readOnly,
  label,
}: {
  proposal: Proposal;
  section: DocumentSection;
  actions: PageActions;
  readOnly: boolean;
  label: string;
}): ReactElement {
  const resolve = useBindings();
  const productId = section.productId;
  const content = section.content;
  const paths = (content.paths ?? []).slice(0, 2);
  const line = productId ? channel(proposal, productId) : null;
  const metrics = productId ? tacticDeliveryMetrics(proposal, productId) : null;
  const priced = productId ? pricedLine(proposal, productId) : null;
  const conversions = priced?.estConversionsMonthly ?? null;
  const headline = content.headline || label;
  const callout = content.callout ?? null;
  const budgetDisplay = line ? money(line.monthlyBudget) : null;

  const audience = audienceTotal(proposal, paths);
  const mix = keywordMix(proposal.mediaPlan.keywords);
  const keywordCount = proposal.mediaPlan.keywords.length;
  const serpModel = searchHeroModel(proposal, section, resolve);
  const serpQuery = resolve(keywordPick(proposal) ?? paths[0]?.searchThemes[0] ?? '');

  const calloutInnerRef = useRef<HTMLDivElement | null>(null);
  const calloutScale = useFitScale(calloutInnerRef, { min: 0.78 });

  // Both path cards share ONE scale (the smaller of their two independent
  // natural fit scales) so side-by-side type always matches — see
  // `useSharedFitScale`'s doc. Two fixed refs (dual_path is always exactly
  // 2 paths per the contract) picked by index below; an absent second path
  // just leaves that ref's `current` null, which the hook skips.
  const pathInnerRef0 = useRef<HTMLDivElement | null>(null);
  const pathInnerRef1 = useRef<HTMLDivElement | null>(null);
  const pathScale = useSharedFitScale(pathInnerRef0, pathInnerRef1, { min: 0.78 });

  return (
    <div className="mv3-dualpath-frame mv3-semplan">
      <div className="mv3-semplan-header">
        <div className="mv3-dualpath-header">
          <div className="mv3-eyebrow">{label}</div>
          <div className="mv3-dualpath-headline">
            <EditableText
              value={headline}
              field="headline"
              placeholder={label}
              readOnly={readOnly}
              variant="headline"
              onSave={(next) => actions.saveContent(section, { headline: next })}
            />
          </div>
          {productId && line ? (
            <div className="mv3-dualpath-budget">
              <span className="mv3-dualpath-budget-label">Monthly budget</span>
              <span className="mv3-dualpath-budget-value">
                <EditableMoney
                  value={line.monthlyBudget}
                  display={budgetDisplay ? `${budgetDisplay} / month` : null}
                  readOnly={readOnly}
                  label={`${label} monthly budget`}
                  onSave={(next) => actions.setChannelBudget(productId, next, label)}
                />
              </span>
            </div>
          ) : null}
        </div>
        {keywordCount > 0 ? (
          <div className="mv3-semplan-mix">
            <div className="mv3-semplan-mix-total">
              <span className="mv3-semplan-mix-value">{count(keywordCount)}</span>
              <span className="mv3-semplan-mix-label">{keywordCount === 1 ? 'keyword' : 'keywords'} in the plan</span>
            </div>
            <div className="mv3-semplan-mix-groups">
              {mix.map((group) => (
                <span className="mv3-semplan-mix-group" key={group.intent}>
                  <strong>{group.count}</strong> {group.intent}
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      <div className="mv3-dualpath-content">
        <div className="mv3-dualpath-cards">
          {paths.map((path, index) => (
            <PathCard
              key={path.id}
              path={path}
              index={index}
              paths={paths}
              proposal={proposal}
              section={section}
              actions={actions}
              readOnly={readOnly}
              innerRef={index === 0 ? pathInnerRef0 : pathInnerRef1}
              scale={pathScale}
            />
          ))}
        </div>
        <div className="mv3-dualpath-rail">
          {serpQuery ? <SponsoredSearchCard model={serpModel} query={serpQuery} /> : null}
          <SemStats
            audience={audience}
            clicks={metrics?.kind === 'cpc' ? metrics.clicks : null}
            conversions={conversions}
          />
          {callout ? (
            <div className="mv3-dualpath-callout mv3-semplan-callout">
              <div className="mv3-dualpath-callout-inner" ref={calloutInnerRef} style={fitScaleStyle(calloutScale)}>
                <div className="mv3-semplan-callout-head">
                  <span className="mv3-dualpath-callout-icon">
                    <ShieldIcon size={22} />
                  </span>
                  <div className="mv3-dualpath-callout-title">{callout.title ? resolve(callout.title) : 'Brand protection'}</div>
                </div>
                <div className="mv3-dualpath-callout-body">
                  <EditableText
                    value={callout.body}
                    field="body"
                    placeholder="Callout body"
                    readOnly={readOnly}
                    onSave={(next) => actions.saveContent(section, { callout: { ...callout, body: next } })}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
